import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FloorPlanDynamic from '../components/FloorPlanDynamic';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import SEO from '../components/SEO';
import { CalendarIcon, Clock, Users, ArrowRight, Zap, MapPin, Tag, Sparkles, Repeat, ChevronRight, ArrowLeft, Phone, HelpCircle } from 'lucide-react';
import { format } from 'date-fns';
import { fr, nl, enUS } from 'date-fns/locale';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import HomeSpacesBlocks from '../components/HomeSpacesBlocks';
import PlanStepper from '../components/PlanStepper';
import HouseRulesDialog from '../components/HouseRulesDialog';
import PaymentRedirectingOverlay from '../components/PaymentRedirectingOverlay';
import Step4Confirmation from '../components/Step4Confirmation';
import SubscriptionUpsell from '../components/SubscriptionUpsell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = Array.from({ length: 33 }, (_, i) => {
  const totalMin = 6 * 60 + i * 30; // 06:00 to 22:00 in 30-min steps
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
});

// Smallest TIME_SLOTS entry that is >= the given Date (Brussels-local).
// Used to grey out past slots when the selected booking date is today.
function nextSlotFromNow(now) {
  const h = now.getHours();
  const m = now.getMinutes();
  const currentMinutes = h * 60 + m;
  for (const slot of TIME_SLOTS) {
    const [sh, sm] = slot.split(':').map(Number);
    if (sh * 60 + sm >= currentMinutes) return slot;
  }
  return null; // past 22:00 — no slot available today
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

export default function FloorPlanPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('nl') ? nl : i18n.language?.startsWith('en') ? enUS : fr;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [selectedSpace, setSelectedSpace] = useState(searchParams.get('space') || null);
  // Wizard: 1 = choose space, 2 = choose time, 3 = order recap + payment method,
  // 4 = post-payment confirmation (access code + recap). We land on step 4
  // when returning from Stripe (?step=4&session_id=…) or PayPal
  // (?step=4&provider=paypal&token=…).
  const initialStep = (() => {
    if (searchParams.get('step') === '4') return 4;
    if (searchParams.get('step') === '3' && searchParams.get('space')) return 3;
    return searchParams.get('space') ? 2 : 1;
  })();
  const [currentStep, setCurrentStep] = useState(initialStep);
  // Post-payment return context (Stripe session or PayPal order)
  const returnSessionId = searchParams.get('session_id');
  const returnPaypalOrderId = searchParams.get('provider') === 'paypal'
    ? searchParams.get('token')
    : null;
  // Full-screen overlay shown while redirecting to Stripe/PayPal
  const [redirectingProvider, setRedirectingProvider] = useState(null); // 'stripe' | 'paypal' | null
  const [date, setDate] = useState(() => {
    const dateParam = searchParams.get('date');
    return dateParam ? new Date(dateParam + 'T00:00:00') : new Date();
  });
  const [bookingType, setBookingType] = useState('hourly');
  const [startTime, setStartTime] = useState(() => searchParams.get('time') || '09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [bookedSpaces, setBookedSpaces] = useState([]);
  const [loadingBooking, setLoadingBooking] = useState(false);
  const [spacesData, setSpacesData] = useState({});
  const [bookingError, setBookingError] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  // End date for daily/half_day multi-day bookings (null = single-day booking).
  // When set to a date > `date`, the booking is submitted via
  // POST /bookings/recurring instead of a single POST /bookings.
  const [endDate, setEndDate] = useState(null);
  const [endCalendarOpen, setEndCalendarOpen] = useState(false);
  const [closedReason, setClosedReason] = useState(null);
  const [freeHoursInfo, setFreeHoursInfo] = useState({ has_subscription: false, balance: 0, monthly_allowance: 0 });
  const [useFreeHours, setUseFreeHours] = useState(0);
  const [voucherInput, setVoucherInput] = useState('');
  const [appliedVoucher, setAppliedVoucher] = useState(null); // {code, discount_type, discount_value}
  const [voucherChecking, setVoucherChecking] = useState(false);
  const [doorHoldOpen, setDoorHoldOpen] = useState(false); // hold the door open during the whole session

  // Live clock — refreshed every second. Used both for the header display
  // and to disable already-past time slots when the user picks "today".
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // When the user switches to a booking type that doesn't support multi-day
  // ranges (weekly only), clear any lingering end date so the payment
  // math stays coherent. Also clear it if the end date has drifted before
  // the start date (e.g., user picks Aug 5 then switches start to Aug 10).
  useEffect(() => {
    if (bookingType === 'weekly') {
      if (endDate) setEndDate(null);
      return;
    }
    if (endDate && date && endDate < date) {
      setEndDate(null);
    }
  }, [bookingType, date, endDate]);

  // List of ISO dates between `date` (inclusive) and `endDate` (inclusive).
  // Returns a single-item list when endDate is null or equal to date.
  const bookingDates = React.useMemo(() => {
    if (!date) return [];
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    const end = endDate ? new Date(endDate) : new Date(start);
    end.setHours(0, 0, 0, 0);
    if (end < start) return [format(start, 'yyyy-MM-dd')];
    const out = [];
    const cur = new Date(start);
    while (cur <= end) {
      out.push(format(cur, 'yyyy-MM-dd'));
      cur.setDate(cur.getDate() + 1);
    }
    // Safety cap — no legit user should ever book more than 60 consecutive days
    return out.slice(0, 60);
  }, [date, endDate]);

  const isMultiDay = (bookingType === 'hourly' || bookingType === 'half_day' || bookingType === 'daily')
    && bookingDates.length > 1;

  const isBookingToday = isSameDay(date, now);
  const minTimeToday = isBookingToday ? nextSlotFromNow(now) : null;
  // No more bookable slots today (past 22:00) — booking is blocked entirely.
  const noSlotsLeftToday = isBookingToday && minTimeToday === null;

  // Auto-bump startTime if the selected hour falls into the past (e.g., user
  // had 09:00 selected, clock just ticked past 09:30, on today's date).
  useEffect(() => {
    if (!isBookingToday) return;
    if (bookingType !== 'hourly' && bookingType !== 'half_day') return;
    if (!minTimeToday) return;
    if (startTime < minTimeToday) {
      setStartTime(minTimeToday);
    }
  }, [isBookingToday, minTimeToday, startTime, bookingType]);

  // Half-day = exactly 4 hours. Every time startTime changes while bookingType
  // is half_day, we auto-shift endTime to startTime+4h so the two selects
  // stay in sync (previously the user had to update endTime manually and
  // often paid for a longer / shorter block than a real "demi-journée").
  useEffect(() => {
    if (bookingType !== 'half_day') return;
    const toH = (t) => { const [h, m] = t.split(':').map(Number); return h + (m || 0) / 60; };
    const target = toH(startTime) + 4;
    if (target > 22) return; // out of business hours (guarded upstream too)
    const hh = String(Math.floor(target)).padStart(2, '0');
    const mm = target % 1 === 0 ? '00' : '30';
    const tt = `${hh}:${mm}`;
    if (TIME_SLOTS.includes(tt) && tt !== endTime) setEndTime(tt);
  }, [bookingType, startTime, endTime]);

  // Make sure endTime stays > startTime after an auto-bump.
  useEffect(() => {
    if (endTime <= startTime) {
      const idx = TIME_SLOTS.indexOf(startTime);
      const next = idx >= 0 && idx + 1 < TIME_SLOTS.length ? TIME_SLOTS[idx + 1] : null;
      if (next) setEndTime(next);
    }
  }, [startTime, endTime]);
  const [houseRulesOpen, setHouseRulesOpen] = useState(false);
  const [pendingProvider, setPendingProvider] = useState(null); // 'stripe' | 'paypal'
  const [paymentDisclaimer, setPaymentDisclaimer] = useState('');
  const [invoiceType, setInvoiceType] = useState('private'); // 'private' | 'business'
  const consentRef = React.useRef(null); // {accepted_at, text} captured at popup confirm

  // Legal billing profile complete? Required to select "business" invoice
  // type (which triggers Peppol UBL XML generation for pro_vat, or an
  // official B2B invoice with BCE for pro_non_vat).
  const billingComplete = React.useMemo(() => {
    if (!user) return false;
    const type = user.customer_type || 'private';
    const hasCompany = Boolean((user.company || '').trim());
    const hasAddress = Boolean((user.address || '').trim());
    if (type === 'pro_vat') {
      return hasCompany && hasAddress && Boolean((user.company_vat || '').trim());
    }
    if (type === 'pro_non_vat') {
      return hasCompany && hasAddress && Boolean((user.company_bce || '').trim());
    }
    return false; // 'private' or unknown → no B2B invoice
  }, [user]);

  // Fetch the admin-editable disclaimer once
  useEffect(() => {
    axios.get(`${API}/site-settings`)
      .then((r) => setPaymentDisclaimer(r.data?.payment_disclaimer || ''))
      .catch(() => {});
  }, []);

  // Fetch free hours balance for logged-in users
  useEffect(() => {
    if (!user || !token) { setFreeHoursInfo({ has_subscription: false, balance: 0, monthly_allowance: 0 }); return; }
    axios.get(`${API}/me/free-hours`, {
      headers: { Authorization: `Bearer ${token}` }, withCredentials: true,
    }).then(res => setFreeHoursInfo(res.data)).catch(() => {});
  }, [user, token]);

  // Fetch spaces from API
  useEffect(() => {
    const fetchSpaces = async () => {
      try {
        const res = await axios.get(`${API}/spaces`);
        const map = {};
        for (const s of res.data) {
          map[s.space_id] = {
            name: s.name,
            capacity: s.capacity,
            hourly: s.price_hourly,
            half_day: s.price_half_day || 0,
            daily: s.price_daily,
            weekly: s.price_weekly,
          };
        }
        setSpacesData(map);
      } catch { /* ignore */ }
    };
    fetchSpaces();
  }, []);

  const selectedInfo = selectedSpace ? spacesData[selectedSpace] : null;

  // Subscription discount
  const isSubscribed = user?.is_subscribed || false;
  const discountPercent = user?.discount_percent || 0;

  const applyDiscount = (price) => {
    if (!isSubscribed || discountPercent <= 0) return price;
    return Math.round(price * (1 - discountPercent / 100) * 100) / 100;
  };

  // Single source of truth for the "final amount the customer will pay" —
  // reused both in the inline booking-total block AND in the pre-payment
  // HouseRulesDialog summary so both stay strictly in sync. Applies the same
  // pipeline the backend uses: subscription discount → voucher → +21% VAT.
  const computeFinalTVAC = () => {
    const base = calculatePrice(); // HT
    const discounted = applyDiscount(base); // HT after subscription discount
    let voucherCut = 0;
    if (appliedVoucher) {
      if (appliedVoucher.discount_type === 'percent') {
        voucherCut = Math.round(discounted * appliedVoucher.discount_value) / 100;
      } else {
        voucherCut = Math.min(appliedVoucher.discount_value, discounted);
      }
    }
    const finalHT = Math.max(Math.round((discounted - voucherCut) * 100) / 100, 0);
    const tva = Math.round(finalHT * 0.21 * 100) / 100;
    return Math.round((finalHT + tva) * 100) / 100;
  };

  useEffect(() => {
    if (date && Object.keys(spacesData).length > 0) {
      const dateStr = format(date, 'yyyy-MM-dd');
      const fetchAvailability = async () => {
        // Check closed status
        try {
          const cres = await axios.get(`${API}/closed-days?start=${dateStr}&end=${dateStr}`);
          if (cres.data && cres.data.length > 0) {
            setClosedReason(cres.data[0].reason || 'Ferme');
          } else {
            setClosedReason(null);
          }
        } catch { setClosedReason(null); }

        const spaceIds = Object.keys(spacesData);
        const booked = [];
        for (const sid of spaceIds) {
          try {
            const res = await axios.get(`${API}/spaces/${sid}/availability?date=${dateStr}`);
            if (res.data.booked_slots.length > 0) {
              const isFullDay = res.data.booked_slots.some(s => s.booking_type === 'daily');
              if (isFullDay) booked.push(sid);
            }
          } catch { /* ignore */ }
        }
        setBookedSpaces(booked);
      };
      fetchAvailability();
    }
  }, [date, spacesData]);

  const VAT_MULT = 1.21;
  const calculatePrice = () => {
    if (!selectedInfo) return 0;
    // Multi-day daily/half_day/hourly: unit price × number of days.
    if (isMultiDay) {
      const days = bookingDates.length;
      if (bookingType === 'daily') return Math.round(selectedInfo.daily * days * 100) / 100;
      if (bookingType === 'half_day') {
        const hd = Number(selectedInfo.half_day) || 0;
        return Math.round(hd * days * 100) / 100;
      }
      if (bookingType === 'hourly') {
        const toH = (t) => {
          const [h, m] = t.split(':').map(Number);
          return h + (m || 0) / 60;
        };
        const hours = Math.max(toH(endTime) - toH(startTime), 0.5);
        return Math.round(selectedInfo.hourly * hours * days * 100) / 100;
      }
    }
    if (bookingType === 'daily') return selectedInfo.daily;
    if (bookingType === 'weekly') return selectedInfo.weekly;
    const toH = (t) => {
      const [h, m] = t.split(':').map(Number);
      return h + (m || 0) / 60;
    };
    const hours = Math.max(toH(endTime) - toH(startTime), 0.5);
    if (bookingType === 'half_day') {
      const hd = Number(selectedInfo.half_day) || 0;
      if (hd <= 0) return Math.round(selectedInfo.hourly * hours * 100) / 100;
      if (hours <= 4) return hd;
      return Math.round((hd + (hours - 4) * selectedInfo.hourly) * 100) / 100;
    }
    return Math.round(selectedInfo.hourly * hours * 100) / 100;
  };

  const handleBook = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (!selectedSpace || !date) {
      toast.error(t('booking.selectSpaceAndDate'));
      return;
    }
    // Defensive: refuse to book a hourly/half-day slot that has already started.
    if ((bookingType === 'hourly' || bookingType === 'half_day')
        && isBookingToday && minTimeToday && startTime < minTimeToday) {
      toast.error(t('booking.startTimeInPast', {
        defaultValue: "L'heure de début est déjà passée. Sélectionnez un créneau futur.",
      }));
      return;
    }

    setLoadingBooking(true);
    setBookingError('');
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Multi-day daily / half_day booking → use the recurring pipeline. This
      // creates N bookings (one per date) and returns a recurring_id which the
      // dedicated /payments/checkout-recurring endpoint knows how to charge in
      // a single Stripe session, so the customer sees ONE checkout for the
      // whole range.
      if (isMultiDay) {
        const recRes = await axios.post(`${API}/bookings/recurring`, {
          space_id: selectedSpace,
          dates: bookingDates,
          start_time: bookingType === 'daily' ? '06:00' : startTime,
          end_time: bookingType === 'daily' ? '22:00' : endTime,
          booking_type: bookingType,
          voucher_code: appliedVoucher ? appliedVoucher.code : null,
          door_hold_open: doorHoldOpen,
          disclaimer_accepted_at: consentRef.current?.accepted_at || null,
          disclaimer_accepted_text: consentRef.current?.text || null,
          invoice_type: (invoiceType === 'business' && billingComplete) ? 'business' : 'private',
        }, { headers, withCredentials: true });

        const { recurring_id, conflicts } = recRes.data || {};
        if (Array.isArray(conflicts) && conflicts.length > 0) {
          toast.warning(t('booking.someConflictsSkipped', {
            defaultValue: `Certaines dates étaient déjà prises et ont été ignorées : ${conflicts.join(', ')}`,
          }));
        }
        const payRes = await axios.post(`${API}/payments/checkout-recurring`, {
          recurring_id,
          origin_url: window.location.origin,
          locale: (i18n.language || 'fr').slice(0, 2),
        }, { headers, withCredentials: true });
        if (payRes.data?.free) {
          toast.success(t('booking.confirmedFree'));
          navigate(`/dashboard`);
          return;
        }
        setRedirectingProvider('stripe');
        window.location.href = payRes.data.url;
        return;
      }

      const res = await axios.post(`${API}/bookings`, {
        space_id: selectedSpace,
        date: dateStr,
        start_time: bookingType === 'daily' ? '06:00' : startTime,
        end_time: bookingType === 'daily' ? '22:00' : endTime,
        booking_type: bookingType,
        use_free_hours: 0,
        voucher_code: appliedVoucher ? appliedVoucher.code : null,
        door_hold_open: doorHoldOpen,
        disclaimer_accepted_at: consentRef.current?.accepted_at || null,
        disclaimer_accepted_text: consentRef.current?.text || null,
        invoice_type: (invoiceType === 'business' && billingComplete) ? 'business' : 'private',
      }, { headers, withCredentials: true });

      const booking = res.data;

      // Proceed to payment
      const payRes = await axios.post(`${API}/payments/checkout`, {
        booking_id: booking.booking_id,
        origin_url: window.location.origin,
        locale: (i18n.language || 'fr').slice(0, 2),
      }, { headers, withCredentials: true });

      // If free (zero-cost), the backend returns { free: true } and confirms directly
      if (payRes.data?.free) {
        toast.success(t('booking.confirmedFree'));
        // Refresh balance and redirect to dashboard
        try {
          const fh = await axios.get(`${API}/me/free-hours`, { headers, withCredentials: true });
          setFreeHoursInfo(fh.data);
        } catch { /* ignore */ }
        navigate(`/dashboard`);
        return;
      }
      // Redirect to Stripe
      setRedirectingProvider('stripe');
      window.location.href = payRes.data.url;
    } catch (err) {
      const msg = err.response?.data?.detail || t('booking.bookingError');
      setBookingError(msg);
      toast.error(msg);
    } finally {
      setLoadingBooking(false);
    }
  };

  // PayPal payment flow
  const requestPayment = (provider) => {
    if (!user) { navigate('/auth'); return; }
    if (!selectedSpace || !date) { toast.error(t('booking.selectSpaceAndDate')); return; }
    if (closedReason) { toast.error(t('booking.closedToday')); return; }
    setPendingProvider(provider);
    setHouseRulesOpen(true);
  };

  const onHouseRulesConfirm = () => {
    // Snapshot the exact disclaimer text + acceptance timestamp for legal archival
    const shownText = (paymentDisclaimer && String(paymentDisclaimer).trim())
      || "En confirmant le paiement, je m'engage à : rendre les locaux dans l'état initial, ranger meubles/chaises/matériel, laisser toilettes propres, sortir mes déchets. Supplément forfaitaire 30 EUR (nettoyage et remise en état) en cas de non-respect.";
    consentRef.current = { accepted_at: new Date().toISOString(), text: shownText };
    setHouseRulesOpen(false);
    const provider = pendingProvider;
    setPendingProvider(null);
    if (provider === 'paypal') handleBookPaypal();
    else handleBook();
  };

  const handleBookPaypal = async () => {
    if (!user) { navigate('/auth'); return; }
    if (!selectedSpace || !selectedInfo) return;
    if (closedReason) { toast.error(t('booking.closedToday')); return; }
    setLoadingBooking(true);
    setBookingError('');
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const headers = { Authorization: `Bearer ${token}` };

      // Multi-day daily / half_day / hourly booking → create N bookings via
      // the recurring endpoint, then obtain the PayPal approval URL for the
      // whole series in one shot (same amount as Stripe checkout).
      if (isMultiDay) {
        const recRes = await axios.post(`${API}/bookings/recurring`, {
          space_id: selectedSpace,
          dates: bookingDates,
          start_time: bookingType === 'daily' ? '06:00' : startTime,
          end_time: bookingType === 'daily' ? '22:00' : endTime,
          booking_type: bookingType,
          voucher_code: appliedVoucher ? appliedVoucher.code : null,
          door_hold_open: doorHoldOpen,
          disclaimer_accepted_at: consentRef.current?.accepted_at || null,
          disclaimer_accepted_text: consentRef.current?.text || null,
          invoice_type: (invoiceType === 'business' && billingComplete) ? 'business' : 'private',
        }, { headers, withCredentials: true });
        const { recurring_id, conflicts } = recRes.data || {};
        if (Array.isArray(conflicts) && conflicts.length > 0) {
          toast.warning(t('booking.someConflictsSkipped', {
            defaultValue: `Certaines dates étaient déjà prises et ont été ignorées : ${conflicts.join(', ')}`,
          }));
        }
        const ppRes = await axios.post(`${API}/payments/paypal/create-order`, {
          recurring_id,
          origin_url: window.location.origin,
          locale: (i18n.language || 'fr').slice(0, 2),
        }, { headers, withCredentials: true });
        if (ppRes.data?.approval_url) {
          setRedirectingProvider('paypal');
          window.location.href = ppRes.data.approval_url;
        } else {
          toast.error('PayPal: ' + t('booking.bookingError'));
        }
        return;
      }

      // 1) Create the booking (pending)
      const bookRes = await axios.post(`${API}/bookings`, {
        space_id: selectedSpace,
        date: dateStr,
        start_time: bookingType === 'daily' ? '06:00' : startTime,
        end_time: bookingType === 'daily' ? '22:00' : endTime,
        booking_type: bookingType,
        use_free_hours: 0,
        voucher_code: appliedVoucher ? appliedVoucher.code : null,
        door_hold_open: doorHoldOpen,
        disclaimer_accepted_at: consentRef.current?.accepted_at || null,
        disclaimer_accepted_text: consentRef.current?.text || null,
        invoice_type: (invoiceType === 'business' && billingComplete) ? 'business' : 'private',
      }, { headers, withCredentials: true });
      const booking = bookRes.data;
      // 2) Create PayPal order
      const ppRes = await axios.post(`${API}/payments/paypal/create-order`, {
        booking_id: booking.booking_id,
        origin_url: window.location.origin,
        locale: (i18n.language || 'fr').slice(0, 2),
      }, { headers, withCredentials: true });
      // 3) Redirect to PayPal approval URL
      if (ppRes.data?.approval_url) {
        setRedirectingProvider('paypal');
        window.location.href = ppRes.data.approval_url;
      } else {
        toast.error('PayPal: ' + t('booking.bookingError'));
      }
    } catch (err) {
      // Detailed error for prod debugging - surface real backend error to the user
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const networkErr = !err.response;
      console.error('[PayPal] Error', { status, detail, err });
      let msg;
      if (networkErr) {
        msg = 'Serveur indisponible. Réessayez dans quelques secondes (le backend démarre peut-être).';
      } else if (detail) {
        msg = `PayPal: ${detail}`;
      } else {
        msg = `PayPal: erreur ${status || ''} - ${t('booking.bookingError')}`;
      }
      setBookingError(msg);
      toast.error(msg);
    } finally {
      setLoadingBooking(false);
    }
  };

  // Admin-only: confirm booking instantly without payment (for tests)
  const isAdmin = user?.role === 'admin' || user?.email === 'dequenne@hotmail.com';
  const handleBookFree = async () => {
    if (!isAdmin) return;
    if (!selectedSpace || !selectedInfo) return;
    const confirmMsg = isMultiDay
      ? `Confirmer cette série de ${bookingDates.length} sessions GRATUITEMENT (test admin) ? Le client recevra un email avec un seul code d'accès pour toute la période.`
      : "Confirmer cette réservation GRATUITEMENT (test admin) ? Le client recevra un email avec un code d'accès comme pour une vraie réservation.";
    if (!window.confirm(confirmMsg)) return;
    setLoadingBooking(true);
    setBookingError('');
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const headers = { Authorization: `Bearer ${token}` };

      // Multi-day free booking → create the series via /bookings/recurring
      // then confirm all rows in one shot via /payments/admin-free-recurring.
      if (isMultiDay) {
        const recRes = await axios.post(`${API}/bookings/recurring`, {
          space_id: selectedSpace,
          dates: bookingDates,
          start_time: bookingType === 'daily' ? '06:00' : startTime,
          end_time: bookingType === 'daily' ? '22:00' : endTime,
          booking_type: bookingType,
          voucher_code: null,
          door_hold_open: doorHoldOpen,
        }, { headers, withCredentials: true });
        const { recurring_id, conflicts } = recRes.data || {};
        if (Array.isArray(conflicts) && conflicts.length > 0) {
          toast.warning(`Dates déjà prises ignorées : ${conflicts.join(', ')}`);
        }
        const freeRes = await axios.post(`${API}/payments/admin-free-recurring`, {
          recurring_id,
        }, { headers, withCredentials: true });
        if (freeRes.data?.free) {
          const n = freeRes.data.sessions_count;
          toast.success(`Série GRATUITE confirmée (${n} sessions) — Code : ${freeRes.data.access_code || '—'}`);
          navigate('/dashboard');
        } else {
          toast.error('Confirmation gratuite échouée');
        }
        return;
      }

      const bookRes = await axios.post(`${API}/bookings`, {
        space_id: selectedSpace,
        date: dateStr,
        start_time: bookingType === 'daily' ? '06:00' : startTime,
        end_time: bookingType === 'daily' ? '22:00' : endTime,
        booking_type: bookingType,
        use_free_hours: 0,
        voucher_code: null,
        door_hold_open: doorHoldOpen,
      }, { headers, withCredentials: true });
      const booking = bookRes.data;
      const freeRes = await axios.post(`${API}/payments/admin-free`, {
        booking_id: booking.booking_id,
        origin_url: window.location.origin,
      }, { headers, withCredentials: true });
      if (freeRes.data?.free) {
        toast.success(`Réservation GRATUITE confirmée — Code : ${freeRes.data.access_code || '—'}`);
        navigate('/dashboard');
      } else {
        toast.error('Confirmation gratuite échouée');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || t('booking.bookingError');
      setBookingError(msg);
      toast.error(msg);
    } finally {
      setLoadingBooking(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="floor-plan-page">
      <SEO titleKey="seo.plan.title" descriptionKey="seo.plan.description" />

      {/* Full-screen overlay while redirecting to Stripe/PayPal */}
      <PaymentRedirectingOverlay
        show={!!redirectingProvider}
        provider={redirectingProvider || 'stripe'}
      />

      {/* Sticky 4-step wizard progress */}
      <PlanStepper
        activeStep={currentStep}
        onNavigate={(n) => {
          // Once on step 4 (post-payment), disable backwards navigation —
          // the booking is confirmed and the wizard is finished.
          if (currentStep === 4) return;
          if (n < currentStep) setCurrentStep(n);
          else if (n === 2 && selectedSpace) setCurrentStep(2);
          else if (n === 3 && selectedSpace && !closedReason) setCurrentStep(3);
          if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        canGoTo={(n) => currentStep !== 4 && (n < currentStep
          || (n === 2 && !!selectedSpace)
          || (n === 3 && !!selectedSpace && !closedReason))}
      />

      {/* ===== STEP 4 : Post-payment confirmation ===== */}
      {currentStep === 4 && (
        <Step4Confirmation
          sessionId={returnSessionId}
          paypalOrderId={returnPaypalOrderId}
          token={token}
          onGoDashboard={() => navigate('/dashboard')}
          onBookAnother={() => {
            // Reset the wizard back to step 1 with a clean URL
            navigate('/plan', { replace: true });
            setCurrentStep(1);
            setSelectedSpace(null);
          }}
        />
      )}

      {/* ===== STEP 1 : Choose a space ===== */}
      {currentStep === 1 && (
        <HomeSpacesBlocks
          selectedSpace={selectedSpace}
          onSelectSpace={setSelectedSpace}
          onNext={() => {
            setCurrentStep(2);
            if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}

      {/* ===== STEP 2 : Choose date/time + type + payment ===== */}
      {currentStep === 2 && (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back to step 1 + selected space recap */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <button
            type="button"
            onClick={() => {
              setCurrentStep(1);
              if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A3C34] hover:text-[#C77D63] transition-colors"
            data-testid="wizard-back-to-step-1"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back', 'Retour')} — {t('landing.step1Title', 'Choisissez un espace')}
          </button>
          {selectedSpace && spacesData[selectedSpace] && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#C77D63]/10 border border-[#C77D63]/25 rounded-full text-xs">
              <span className="text-[#737373]">{t('booking.yourChoice', 'Votre choix')}:</span>
              <span className="font-semibold text-[#1A3C34]">
                {spacesData[selectedSpace].name || t(`spaces.names.${selectedSpace}`, { defaultValue: selectedSpace })}
              </span>
            </div>
          )}
        </div>

        {/* Compact recurring booking strip */}
        <div className="mb-6 bg-white border border-[#E2E2DF] rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-testid="home-recurring-block">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#1A3C34]/10 flex items-center justify-center">
            <Repeat className="w-4 h-4 text-[#1A3C34]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#1A3C34]">
              {t('landing.recurringTitle', 'Location récurrente')}
            </div>
            <div className="text-xs text-[#737373]">
              {t('landing.recurringShort', 'Plusieurs dates en une fois, un seul paiement, une seule facture.')}
            </div>
          </div>
          <button
            onClick={() => navigate(
              selectedSpace ? `/recurrente?space=${encodeURIComponent(selectedSpace)}` : '/recurrente'
            )}
            className="text-xs font-semibold text-[#C77D63] hover:text-[#b06a51] whitespace-nowrap flex items-center gap-1"
            data-testid="home-cta-recurring"
          >
            {t('landing.recurringCta', 'Réserver plusieurs dates')} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="mb-6 flex justify-end">
          {/* Live clock has been moved to the navbar (LiveClockBadge). */}
        </div>

        <div className="grid lg:grid-cols-1 gap-8">
          {/* Booking panel - centered single column since the interactive plan is removed */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* Date + Type + Hours in one panel — emphasized with the same nice frame as the rate card.
                  Sections are reordered visually via `order-*` so the user sees:
                  1) Dates de réservation  2) Type de réservation  3) Heures de réservation */}
              <div className="bg-white border-2 border-[#1A3C34] rounded-xl p-5 flex flex-col gap-5 shadow-sm" data-testid="booking-date-panel">
                {/* Type de réservation */}
                <div className="order-2 border-t border-[#E2E2DF] pt-4" data-testid="type-section">
                  <h3 className="font-body text-sm font-semibold text-[#1A3C34] mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {t('booking.type')}
                  </h3>
                  <Select value={bookingType} onValueChange={(v) => {
                    setBookingType(v);
                    // When switching to half-day: (a) clamp start to <=18:00
                    // (space closes at 22:00, so 18:00 is the latest valid
                    // start), (b) pre-fill end_time to start_time + 4h.
                    if (v === 'half_day') {
                      let s = startTime;
                      if (s > '18:00') {
                        s = '18:00';
                        setStartTime(s);
                      }
                      const toH = (t) => { const [h, m] = t.split(':').map(Number); return h + (m || 0) / 60; };
                      const target = toH(s) + 4;
                      const tt = `${String(Math.floor(target)).padStart(2, '0')}:${target % 1 === 0 ? '00' : '30'}`;
                      if (TIME_SLOTS.includes(tt)) setEndTime(tt);
                    }
                  }}>
                    <SelectTrigger data-testid="booking-type-select" className="h-11 border-[#E2E2DF] rounded-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">{t('booking.hourly')}</SelectItem>
                      <SelectItem value="half_day" data-testid="booking-type-half-day">
                        {t('booking.halfDay', { defaultValue: 'Demi-journée (4h)' })}
                      </SelectItem>
                      <SelectItem value="daily">{t('booking.daily')}</SelectItem>
                      <SelectItem value="weekly">{t('booking.weekly')}</SelectItem>
                    </SelectContent>
                  </Select>

                  {bookingType === 'half_day' && (
                    <p className="mt-2 text-[11px] text-[#737373] leading-snug bg-[#F9F9F7] border border-[#E2E2DF] rounded-md px-2.5 py-1.5">
                      {t('booking.halfDayHint', {
                        defaultValue: 'Tarif demi-journée pour 4h. Au-delà, chaque heure supplémentaire est facturée au tarif horaire.',
                      })}
                    </p>
                  )}
                  {bookingType === 'daily' && (
                    <p className="mt-2 text-[11px] text-[#737373] leading-snug bg-[#F9F9F7] border border-[#E2E2DF] rounded-md px-2.5 py-1.5">
                      {t('booking.dailyHint', {
                        defaultValue: 'Une journée = de 06h00 à 22h00 (16h).',
                      })}
                    </p>
                  )}
                </div>

                {/* Heures de réservation — only for hourly / half-day bookings */}
                {(bookingType === 'hourly' || bookingType === 'half_day') && (
                <div className="order-3 border-t border-[#E2E2DF] pt-4" data-testid="hours-section">
                  <h3 className="font-body text-sm font-semibold text-[#1A3C34] mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {t('booking.hoursSectionTitle', { defaultValue: 'Heures de réservation' })}
                  </h3>
                    <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[#737373] mb-1 block">{t('booking.startLabel')}</label>
                      <Select value={startTime} onValueChange={setStartTime}>
                        <SelectTrigger data-testid="start-time-select" className="h-10 border-[#E2E2DF] rounded-md text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_SLOTS.slice(0, -1).map(slot => {
                            // Disable past slots when the user is booking for today.
                            const isPast = isBookingToday && minTimeToday && slot < minTimeToday;
                            // Half-day = start+4h. Space closes at 22:00, so
                            // the latest valid half-day start is 18:00 (18+4=22).
                            const isTooLateForHalfDay = bookingType === 'half_day' && slot > '18:00';
                            const disabled = isPast || isTooLateForHalfDay;
                            const suffix = isPast
                              ? ` · ${t('booking.pastSlot', { defaultValue: 'passé' })}`
                              : isTooLateForHalfDay
                                ? ` · ${t('booking.tooLateHalfDay', { defaultValue: 'fermeture 22h' })}`
                                : '';
                            return (
                              <SelectItem
                                key={slot}
                                value={slot}
                                disabled={disabled}
                                data-testid={`start-time-option-${slot}`}
                                className={disabled ? 'opacity-40 line-through' : ''}
                              >
                                {slot}{suffix}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-[#737373] mb-1 block">
                        {t('booking.endLabel')}
                        {bookingType === 'half_day' && (
                          <span className="ml-1.5 text-[10px] text-[#737373]/70 italic">
                            ({t('booking.autoEnd', { defaultValue: 'auto +4h' })})
                          </span>
                        )}
                      </label>
                      <Select
                        value={endTime}
                        onValueChange={setEndTime}
                        disabled={bookingType === 'half_day'}
                      >
                        <SelectTrigger
                          data-testid="end-time-select"
                          className={`h-10 border-[#E2E2DF] rounded-md text-sm ${bookingType === 'half_day' ? 'bg-[#F9F9F7] cursor-not-allowed' : ''}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_SLOTS.filter(t => t > startTime).map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                {noSlotsLeftToday && (
                  <p
                    className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                    data-testid="no-slots-left-today"
                  >
                    {t('booking.noSlotsLeftToday', {
                      defaultValue: "Plus de créneau disponible aujourd'hui (passé 22h00). Choisissez une autre date dans le calendrier.",
                    })}
                  </p>
                )}
                </div>
                )}

                {/* Dates de réservation — first section */}
                <div className="order-1" data-testid="date-section">
                  <h3 className="font-heading text-lg font-bold text-[#1A3C34] mb-3 flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5" />
                    {t('booking.datesSectionTitle', { defaultValue: 'Dates de réservation' })}
                  </h3>
                  {(bookingType === 'hourly' || bookingType === 'half_day' || bookingType === 'daily') ? (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[11px] text-[#737373] mb-1 block uppercase tracking-wide">
                          {t('booking.from', { defaultValue: 'Du' })}
                        </label>
                        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              data-testid="date-picker-trigger"
                              variant="outline"
                              className="w-full justify-start text-left font-normal h-11 border-[#E2E2DF] rounded-md text-xs sm:text-sm"
                            >
                              <CalendarIcon className="w-4 h-4 mr-2 text-[#737373] flex-shrink-0" />
                              <span className="truncate">
                                {date ? format(date, 'PPP', { locale: dateLocale }) : t('booking.chooseDate')}
                              </span>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 z-50" align="start" sideOffset={4}>
                            <Calendar
                              mode="single"
                              locale={dateLocale}
                              selected={date}
                              onSelect={(d) => { setDate(d); setCalendarOpen(false); }}
                              disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <label className="text-[11px] text-[#737373] mb-1 block uppercase tracking-wide">
                          {t('booking.to', { defaultValue: 'Au (optionnel)' })}
                        </label>
                        <Popover open={endCalendarOpen} onOpenChange={setEndCalendarOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              data-testid="date-picker-end-trigger"
                              variant="outline"
                              className="w-full justify-start text-left font-normal h-11 border-[#E2E2DF] rounded-md text-xs sm:text-sm"
                            >
                              <CalendarIcon className="w-4 h-4 mr-2 text-[#737373] flex-shrink-0" />
                              <span className={`truncate ${endDate ? '' : 'text-[#9A9A9A]'}`}>
                                {endDate ? format(endDate, 'PPP', { locale: dateLocale })
                                  : t('booking.singleDay', { defaultValue: 'Un seul jour' })}
                              </span>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 z-50" align="start" sideOffset={4}>
                            <div className="p-2 flex items-center justify-between border-b border-[#E2E2DF]">
                              <span className="text-[11px] text-[#737373]">
                                {endDate
                                  ? t('booking.endDateSelected', { defaultValue: 'Date de fin' })
                                  : t('booking.endDatePickHint', { defaultValue: "Choisissez une date pour réserver plusieurs jours" })}
                              </span>
                              {endDate && (
                                <button
                                  type="button"
                                  onClick={() => { setEndDate(null); setEndCalendarOpen(false); }}
                                  data-testid="date-picker-end-clear"
                                  className="text-[11px] text-[#C77D63] hover:underline font-medium"
                                >
                                  {t('booking.clear', { defaultValue: 'Effacer' })}
                                </button>
                              )}
                            </div>
                            <Calendar
                              mode="single"
                              locale={dateLocale}
                              selected={endDate}
                              onSelect={(d) => { setEndDate(d); setEndCalendarOpen(false); }}
                              disabled={(d) => d < (date || new Date(new Date().setHours(0,0,0,0)))}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  ) : (
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          data-testid="date-picker-trigger"
                          variant="outline"
                          className="w-full justify-start text-left font-normal h-11 border-[#E2E2DF] rounded-md"
                        >
                          <CalendarIcon className="w-4 h-4 mr-2 text-[#737373]" />
                          {date ? format(date, 'PPP', { locale: dateLocale }) : t('booking.chooseDate')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-50" align="start" sideOffset={4}>
                        <Calendar
                          mode="single"
                          locale={dateLocale}
                          selected={date}
                          onSelect={(d) => { setDate(d); setCalendarOpen(false); }}
                          disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  )}

                  {/* Multi-day summary — shown as soon as endDate > date */}
                  {isMultiDay && (
                    <div
                      className="mt-3 flex items-center justify-between bg-[#1A3C34]/5 border border-[#1A3C34]/15 rounded-md px-3 py-2"
                      data-testid="multi-day-summary"
                    >
                      <span className="text-xs text-[#1A3C34] font-medium">
                        {t('booking.multiDayRange', {
                          defaultValue: `Du ${format(date, 'd MMM', { locale: dateLocale })} au ${format(endDate, 'd MMM yyyy', { locale: dateLocale })}`,
                          from: format(date, 'd MMM', { locale: dateLocale }),
                          to: format(endDate, 'd MMM yyyy', { locale: dateLocale }),
                        })}
                      </span>
                      <span className="text-[11px] text-[#737373] font-mono tabular-nums">
                        {bookingDates.length} {t('booking.days', { defaultValue: 'jours' })}
                      </span>
                    </div>
                  )}
                  {closedReason && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2" data-testid="closed-day-banner">
                      <span className="bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider rounded px-2 py-0.5 flex-shrink-0 mt-0.5">{t('closed.label')}</span>
                      <div className="text-xs text-red-900">
                        <p className="font-semibold">{t('closed.unavailable')}</p>
                        <p className="opacity-80">{closedReason}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Simple "Payer" button — leads to step 3 (payment method choice) */}
              {selectedInfo && (
                <div className="pt-2" data-testid="step2-to-payment-cta">
                  <Button
                    data-testid="step2-continue-btn"
                    disabled={!!closedReason}
                    onClick={() => {
                      setCurrentStep(3);
                      if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-full h-14 bg-[#C77D63] text-white hover:bg-[#b06a51] rounded-lg font-bold text-base shadow-lg shadow-[#C77D63]/25 transition-all"
                  >
                    <span className="flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      {t('booking.reviewAndPay', { defaultValue: 'Vérifiez et payer' })}
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  </Button>
                  {closedReason && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2" data-testid="closed-day-banner-step2">
                      <span className="bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider rounded px-2 py-0.5 flex-shrink-0 mt-0.5">{t('closed.label')}</span>
                      <div className="text-xs text-red-900">
                        <p className="font-semibold">{t('closed.unavailable')}</p>
                        <p className="opacity-80">{closedReason}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!selectedInfo && (
                <div className="bg-white border border-dashed border-[#E2E2DF] rounded-xl p-8 text-center" data-testid="no-space-selected">
                  <MapPin className="w-8 h-8 text-[#E2E2DF] mx-auto mb-3" />
                  <p className="text-sm text-[#737373]">
                    {t('booking.selectSpaceHint')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* ===== STEP 3 : Order recap + payment method choice ===== */}
      {currentStep === 3 && (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => {
              setCurrentStep(2);
              if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A3C34] hover:text-[#C77D63] transition-colors"
            data-testid="wizard-back-to-step-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back', 'Retour')} — {t('landing.step2Title', 'Choisissez un horaire')}
          </button>
          {selectedInfo && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#C77D63]/10 border border-[#C77D63]/25 rounded-full text-xs">
              <span className="text-[#737373]">{t('booking.yourChoice', 'Votre choix')}:</span>
              <span className="font-semibold text-[#1A3C34]">
                {selectedInfo.name || t(`spaces.names.${selectedSpace}`, { defaultValue: selectedSpace })}
              </span>
            </div>
          )}
        </div>

        {selectedInfo && (
          <div className="space-y-6" data-testid="step3-payment">
            <div className="bg-white border-2 border-[#1A3C34]/15 rounded-xl p-5 shadow-sm" data-testid="step3-recap-card">
                  {/* Voucher / Promo code — top of the card, publicly usable (no login required) */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4 text-[#1A3C34]" />
                      <h3 className="font-body text-sm font-semibold text-[#1A3C34]">
                        {t('booking.promoCodeTitle', { defaultValue: 'Code promo' })}
                      </h3>
                    </div>
                    <p className="text-xs text-[#737373] leading-relaxed mb-3">
                      {t('booking.promoCodeIntro', {
                        defaultValue: "Vous disposez d'un code promo ? Saisissez-le ci-dessous pour bénéficier automatiquement de la réduction sur votre réservation.",
                      })}
                    </p>
                    <div className="mb-4" data-testid="voucher-input-block">
                      {!appliedVoucher ? (
                        <div className="flex gap-2 rounded-lg border-2 border-[#1A3C34] bg-white p-2 shadow-sm">
                          <Input
                            value={voucherInput}
                            onChange={(e) => setVoucherInput(e.target.value.toUpperCase())}
                            placeholder={t('booking.promoCode')}
                            className="h-10 flex-1 text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 uppercase font-mono bg-transparent"
                            data-testid="voucher-input"
                          />
                          <Button
                            type="button"
                            disabled={voucherChecking || !voucherInput.trim()}
                            onClick={async () => {
                              setVoucherChecking(true);
                              try {
                                const res = await axios.post(`${API}/vouchers/validate`, { code: voucherInput.trim() });
                                setAppliedVoucher(res.data);
                                toast.success(t('booking.codeApplied', { code: res.data.code }));
                              } catch (err) {
                                toast.error(err.response?.data?.detail || t('booking.invalidCode'));
                              } finally { setVoucherChecking(false); }
                            }}
                            className="h-10 bg-[#1A3C34] text-white hover:bg-[#0f2924] text-xs font-bold px-4"
                            data-testid="voucher-apply-btn"
                          >
                            {t('booking.applyCode')}
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between rounded-lg border-2 border-[#1A3C34] bg-[#C77D63]/10 px-3 py-2" data-testid="voucher-applied">
                          <div>
                            <span className="font-mono text-sm font-bold text-[#C77D63]">{appliedVoucher.code}</span>
                            <span className="ml-2 text-xs text-[#737373]">
                              {appliedVoucher.discount_type === 'percent' ? `-${appliedVoucher.discount_value}%` : `-${appliedVoucher.discount_value} EUR`}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => { setAppliedVoucher(null); setVoucherInput(''); }}
                            className="text-[#737373] hover:text-red-500 text-xs"
                          >
                            {t('booking.remove')}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Try-the-space free session offer — compact & neutral style (same tone as the promo-code frame) */}
                    <div
                      className="rounded-lg border border-[#1A3C34]/20 bg-[#F0EFEA] p-3 mb-4"
                      data-testid="free-trial-cta"
                    >
                      <div className="flex items-start gap-2.5">
                        <Sparkles className="w-3.5 h-3.5 text-[#C77D63] flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-[#1A3C34] leading-snug">
                            <span className="font-semibold">
                              {t('booking.freeTrialTitle', { defaultValue: 'Une formation ? Un cours ? Un projet ?' })}
                            </span>{' '}
                            <span className="text-[#737373]">
                              {t('booking.freeTrialBodyShort', {
                                defaultValue: "Appelez-nous par téléphone pour essayer l'espace gratuitement !",
                              })}
                            </span>
                          </p>
                          <a
                            href={`tel:${t('booking.freeTrialPhoneTel', { defaultValue: '+32497849126' })}`}
                            className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#C77D63] hover:text-[#b06a51] transition-colors"
                            data-testid="free-trial-phone-btn"
                          >
                            <Phone className="w-3 h-3" />
                            {t('booking.freeTrialPhoneDisplay', { defaultValue: '0497 84 91 26' })}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-[#E2E2DF]">
                    {/* Door hold-open option — visible only for the Grande Salle
                        (only room where "conference mode" makes sense with latecomers) */}
                    {selectedSpace === 'grande-salle' && (
                    <label
                      className="mb-4 flex items-start gap-3 cursor-pointer bg-[#1A3C34]/5 border border-[#1A3C34]/10 rounded-lg p-3 hover:bg-[#1A3C34]/10 transition-colors"
                      data-testid="door-hold-open-block"
                    >
                      <input
                        type="checkbox"
                        checked={doorHoldOpen}
                        onChange={(e) => setDoorHoldOpen(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-[#1A3C34]"
                        data-testid="door-hold-open-checkbox"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-semibold text-[#1A3C34]">
                            {t('booking.doorHoldOpen', { defaultValue: 'Laisser la porte ouverte pendant toute la session' })}
                          </span>
                          <TooltipProvider delayDuration={100}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={(e) => e.preventDefault()}
                                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#1A3C34]/10 text-[#1A3C34] hover:bg-[#1A3C34]/20 transition-colors"
                                  aria-label={t('booking.doorHoldOpenHelp', { defaultValue: 'En savoir plus' })}
                                  data-testid="door-hold-open-help"
                                >
                                  <HelpCircle className="w-3 h-3" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-[260px] bg-[#1A3C34] text-white text-[11px] leading-snug p-2.5"
                              >
                                {t('booking.doorHoldOpenHint', {
                                  defaultValue: "Idéal pour les conférences. Vous pourrez basculer ouvert/fermé depuis votre tableau de bord pendant la séance, sans que vos invités aient à taper un code.",
                                })}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <span className="text-[10px] bg-[#C77D63]/15 text-[#C77D63] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide">
                            {t('booking.confMode', { defaultValue: 'mode conf' })}
                          </span>
                        </div>
                      </div>
                    </label>
                    )}

                    <div className="mb-4 space-y-1.5 hidden" data-testid="booking-total-block" aria-hidden="true"></div>
                    {/* Savings banner — total amount + % saved vs catalog price */}
                    {(() => {
                      const base = calculatePrice();
                      const discounted = applyDiscount(base);
                      let afterVoucher = discounted;
                      if (appliedVoucher) {
                        const cut = appliedVoucher.discount_type === 'percent'
                          ? Math.round(discounted * appliedVoucher.discount_value) / 100
                          : Math.min(appliedVoucher.discount_value, discounted);
                        afterVoucher = Math.max(discounted - cut, 0);
                      }
                      const finalAmt = Math.max(Math.round(afterVoucher * 100) / 100, 0);
                      const savings = Math.round((base - finalAmt) * 100) / 100;
                      if (savings <= 0.01 || base <= 0) return null;
                      const pct = Math.round((savings / base) * 100);
                      return (
                        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-center gap-2" data-testid="booking-savings">
                          <Sparkles className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                          <span className="text-xs text-emerald-900">
                            {t('booking.savings', {
                              defaultValue: 'Vous économisez {{amount}} EUR ({{pct}}%) HT sur le tarif catalogue',
                              amount: savings.toFixed(2),
                              pct,
                            })}
                          </span>
                        </div>
                      );
                    })()}
                    {bookingError && (
                      <div data-testid="booking-error" className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                        {bookingError}
                      </div>
                    )}
                    {/* ===== ORDER RECAP — visible right before the pay button ===== */}
                    <div className="mb-4 bg-[#1A3C34] rounded-xl p-4 sm:p-5 text-white" data-testid="order-recap">
                      <div className="flex items-center gap-2 mb-3">
                        <Zap className="w-4 h-4 text-[#C77D63]" />
                        <span className="text-xs uppercase tracking-widest font-bold text-[#C77D63]">
                          {t('booking.orderRecap', { defaultValue: 'Récapitulation' })}
                        </span>
                      </div>
                      <dl className="space-y-1.5 text-sm">
                        <div className="flex justify-between gap-4">
                          <dt className="text-white/60">{t('booking.space', 'Espace')}</dt>
                          <dd className="font-semibold text-right truncate">
                            {selectedInfo.name || t(`spaces.names.${selectedSpace}`, { defaultValue: selectedSpace })}
                            <span className="text-white/50 font-normal ml-1">· {selectedInfo.capacity} pers.</span>
                          </dd>
                        </div>
                        <div className="flex justify-between gap-4">
                          <dt className="text-white/60">{t('booking.type', 'Type')}</dt>
                          <dd className="font-semibold text-right">
                            {bookingType === 'hourly' && t('booking.hourly', { defaultValue: "À l'heure" })}
                            {bookingType === 'half_day' && t('booking.halfDayLabel', { defaultValue: 'Demi-journée (4h)' })}
                            {bookingType === 'daily' && t('booking.daily', { defaultValue: 'Journée' })}
                            {bookingType === 'weekly' && t('booking.weekly', { defaultValue: 'Semaine' })}
                          </dd>
                        </div>
                        {(bookingType === 'hourly' || bookingType === 'half_day') && (
                          <div className="flex justify-between gap-4">
                            <dt className="text-white/60">{t('booking.hours', 'Horaires')}</dt>
                            <dd className="font-mono font-semibold text-right">{startTime} — {endTime}</dd>
                          </div>
                        )}
                        {bookingType === 'daily' && (
                          <div className="flex justify-between gap-4">
                            <dt className="text-white/60">{t('booking.hours', 'Horaires')}</dt>
                            <dd className="font-mono font-semibold text-right">06:00 — 22:00</dd>
                          </div>
                        )}
                        <div className="flex justify-between gap-4">
                          <dt className="text-white/60">{t('booking.dates', 'Date(s)')}</dt>
                          <dd className="font-semibold text-right capitalize">
                            {bookingDates.length > 1
                              ? `${format(date, 'd MMM', { locale: dateLocale })} — ${format(endDate, 'd MMM yyyy', { locale: dateLocale })} (${bookingDates.length} j)`
                              : format(date, 'EEEE d MMMM yyyy', { locale: dateLocale })}
                          </dd>
                        </div>
                        {appliedVoucher && (
                          <div className="flex justify-between gap-4">
                            <dt className="text-white/60">{t('booking.voucher', 'Code promo')}</dt>
                            <dd className="font-semibold text-[#C77D63] text-right">{appliedVoucher.code}</dd>
                          </div>
                        )}
                      </dl>
                      {/* Price breakdown (sub-total HT, VAT, Total TVAC) */}
                      {(() => {
                        const base = calculatePrice(); // HT
                        const discounted = applyDiscount(base); // HT after subscription discount
                        let afterVoucher = discounted;
                        let voucherCut = 0;
                        if (appliedVoucher) {
                          if (appliedVoucher.discount_type === 'percent') {
                            voucherCut = Math.round(discounted * appliedVoucher.discount_value) / 100;
                          } else {
                            voucherCut = Math.min(appliedVoucher.discount_value, discounted);
                          }
                          afterVoucher = Math.max(discounted - voucherCut, 0);
                        }
                        const finalHT = Math.max(Math.round(afterVoucher * 100) / 100, 0);
                        const tva = Math.round(finalHT * 0.21 * 100) / 100;
                        const finalTVAC = Math.round((finalHT + tva) * 100) / 100;
                        const hasReduction = isSubscribed || appliedVoucher;
                        return (
                          <div className="mt-3 pt-3 border-t border-white/15 space-y-1" data-testid="recap-price-breakdown">
                            <div className="flex justify-between text-xs text-white/60">
                              <span>{t('booking.subtotalHT', { defaultValue: 'Sous-total HT' })}</span>
                              <span className="font-mono">
                                {hasReduction && (
                                  <span className="line-through text-white/40 mr-2">{base.toFixed(2)} EUR</span>
                                )}
                                <span className={hasReduction ? 'text-[#C77D63] font-semibold' : 'text-white'}>
                                  {finalHT.toFixed(2)} EUR
                                </span>
                              </span>
                            </div>
                            <div className="flex justify-between text-xs text-white/60">
                              <span>{t('booking.vatLine', { defaultValue: 'TVA 21%' })}</span>
                              <span className="font-mono text-white">+{tva.toFixed(2)} EUR</span>
                            </div>
                            <div className="flex items-center justify-between pt-2 mt-1 border-t border-white/15">
                              <span className="text-xs text-white/60 uppercase tracking-wider font-semibold">
                                {t('booking.totalTvac', { defaultValue: 'Total TVAC' })}
                              </span>
                              <span
                                className="font-heading text-2xl font-bold text-[#C77D63]"
                                data-testid="booking-total-tvac"
                              >
                                {finalTVAC.toFixed(2)} EUR
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    {/* Subscription upsell — visible only for non-subscribers,
                        between the recap card and the pay buttons */}
                    {!isSubscribed && (
                      <div className="mb-4" data-testid="step3-subscription-upsell">
                        <SubscriptionUpsell />
                      </div>
                    )}
                    <Button
                      data-testid="book-now-btn"
                      onClick={() => requestPayment('stripe')}
                      disabled={loadingBooking || !!closedReason}
                      className="w-full h-14 bg-[#C77D63] text-white hover:bg-[#b06a51] rounded-lg font-bold text-base shadow-lg shadow-[#C77D63]/25 transition-all"
                    >
                      {loadingBooking ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          {t('common.loading')}
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Zap className="w-5 h-5" />
                          {t('booking.pay', 'Payer')} · {computeFinalTVAC().toFixed(2)} EUR
                          <ArrowRight className="w-5 h-5" />
                        </span>
                      )}
                    </Button>
                    <Button
                      data-testid="book-paypal-btn"
                      onClick={() => requestPayment('paypal')}
                      disabled={loadingBooking || !!closedReason}
                      variant="outline"
                      className="w-full h-12 mt-2 border-[#FFC439] bg-[#FFC439] hover:bg-[#FFB300] text-[#1A3C34] rounded-lg font-bold transition-all"
                    >
                      <span className="flex items-center gap-2">
                        <span className="font-bold">Pay</span><span className="font-bold italic">Pal</span>
                      </span>
                    </Button>
                    {isAdmin && (
                      <Button
                        data-testid="book-free-admin-btn"
                        onClick={handleBookFree}
                        disabled={loadingBooking || !!closedReason}
                        variant="outline"
                        className="w-full h-11 mt-2 border-dashed border-[#1A3C34]/40 bg-[#F9F9F7] hover:bg-[#1A3C34]/5 text-[#1A3C34] rounded-lg text-sm transition-all"
                      >
                        <span className="flex items-center gap-2">
                          🛡️ <span>Gratuit (test admin)</span>
                        </span>
                      </Button>
                    )}
                    {!user && (
                      <p className="mt-2 text-xs text-center text-[#737373]">
                        {t('booking.loginToBook')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              )}
            </div>
          )}
      <HouseRulesDialog
        open={houseRulesOpen}
        message={paymentDisclaimer}
        isLoading={loadingBooking}
        summary={houseRulesOpen && selectedInfo ? (() => {
          const days = bookingDates.length;
          const isRange = days > 1;
          const timeLabel = (bookingType === 'hourly' || bookingType === 'half_day')
            ? `${startTime}–${endTime}`
            : (bookingType === 'daily' ? '06:00–22:00' : null);
          let datesLabel;
          if (isRange) {
            datesLabel = t('booking.multiDayRange', {
              defaultValue: `Du ${format(date, 'd MMM', { locale: dateLocale })} au ${format(endDate, 'd MMM yyyy', { locale: dateLocale })}`,
              from: format(date, 'd MMM', { locale: dateLocale }),
              to: format(endDate, 'd MMM yyyy', { locale: dateLocale }),
            });
          } else if (bookingType === 'weekly') {
            datesLabel = `${t('booking.weekly', { defaultValue: 'Semaine' })} ${format(date, 'd MMMM yyyy', { locale: dateLocale })}`;
          } else {
            datesLabel = format(date, 'EEEE d MMMM yyyy', { locale: dateLocale });
          }
          return {
            space_name: selectedInfo.name || t('booking.space', { defaultValue: 'Espace' }),
            sessions_count: days,
            time_label: timeLabel,
            dates_label: datesLabel,
            total: computeFinalTVAC(),
          };
        })() : null}
        onClose={() => { setHouseRulesOpen(false); setPendingProvider(null); }}
        onConfirm={onHouseRulesConfirm}
        billingComplete={billingComplete}
        invoiceType={invoiceType}
        setInvoiceType={setInvoiceType}
      />
    </div>
  );
}
